import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    Clock3,
    Loader2,
    RefreshCw,
    ServerCog,
    ShieldCheck,
    TimerReset,
    Zap,
    type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import {
    agentApi,
    type SearchV3AutoSourceConfigOps,
    type SearchV3AutoSourceOpsHealth,
    type SearchV3AutoSourceOpsResponse,
    type SearchV3AutoSourceRunOps,
    type SearchV3AutoSourceTick,
    type SearchV3AutoSourceWorkerRunOps,
} from '@/lib/agent.api';
import { getApiErrorMessage } from '@/lib/http';

const POLL_MS = 30000;

const C = {
    ink: '#1E2428',
    muted: '#66717A',
    faint: '#8B96A0',
    line: '#E2E7EC',
    bg: '#FAFAF8',
    panel: 'rgba(255, 255, 255, 0.82)',
    panelSolid: '#FFFFFF',
    header: 'rgba(255, 255, 255, 0.74)',
    rowHover: '#F8FAFC',
    coral: '#FD775E',
    coralSoft: '#FFF0EC',
    green: '#1D7D5C',
    greenSoft: '#EAF7F0',
    amber: '#A56517',
    amberSoft: '#FFF4DF',
    red: '#B83D2F',
    redSoft: '#FFF0EE',
    blue: '#2F6FBA',
    blueSoft: '#EAF2FB',
    violet: '#6F58B4',
    violetSoft: '#F2EEFB',
    gray: '#65717B',
    graySoft: '#F0F2F3',
} as const;

function isStaffEmail(email?: string | null) {
    const value = String(email || '').toLowerCase();
    return value.endsWith('@ourlanterns.com') || value.endsWith('@careerhackers.io');
}

function formatNumber(value: number) {
    if (!Number.isFinite(value)) return '0';
    return value.toLocaleString();
}

function formatDate(value?: string | null) {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function formatAgeMs(value?: number | null) {
    if (value == null || !Number.isFinite(value)) return 'Never';
    const seconds = Math.max(0, Math.floor(value / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

function ageFromDate(value?: string | null) {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return formatAgeMs(Date.now() - date.getTime());
}

function formatDuration(value?: number | null) {
    if (value == null || !Number.isFinite(value)) return '0ms';
    if (value < 1000) return `${Math.max(0, Math.round(value))}ms`;
    const seconds = value / 1000;
    if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes}m ${rest}s`;
}

function formatHour(hour?: number | null) {
    if (!Number.isFinite(Number(hour))) return 'Unscheduled';
    return new Date(Date.UTC(2026, 0, 1, Number(hour), 0, 0, 0)).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'UTC',
    });
}

function statusTone(status?: string | null) {
    const normalized = String(status || '').toLowerCase();
    if (['completed', 'queued', 'active', 'ok'].includes(normalized)) {
        return { fg: C.green, bg: C.greenSoft };
    }
    if (['running', 'partial', 'claimed'].includes(normalized)) {
        return { fg: C.blue, bg: C.blueSoft };
    }
    if (['warning', 'exhausted', 'waiting_for_user', 'active_run_timeout', 'calibration_required'].includes(normalized)) {
        return { fg: C.amber, bg: C.amberSoft };
    }
    if (['failed', 'cancelled', 'not_observed', 'out_of_leads'].includes(normalized)) {
        return { fg: C.red, bg: C.redSoft };
    }
    return { fg: C.gray, bg: C.graySoft };
}

function healthMeta(health: SearchV3AutoSourceOpsHealth) {
    if (health === 'ok') return { label: 'Healthy', icon: CheckCircle2, fg: C.green, bg: C.greenSoft };
    if (health === 'warning') return { label: 'Attention', icon: AlertTriangle, fg: C.amber, bg: C.amberSoft };
    return { label: 'Not observed', icon: AlertTriangle, fg: C.red, bg: C.redSoft };
}

function searchLabel(row: {
    name?: string | null;
    chat?: { title?: string | null } | null;
    config?: { name?: string | null } | null;
}) {
    return row.name || row.chat?.title || row.config?.name || 'Untitled search';
}

function ownerLabel(row: {
    organization?: { name?: string | null } | null;
    user?: { email?: string | null; name?: string | null } | null;
}) {
    return [row.organization?.name, row.user?.email || row.user?.name].filter(Boolean).join(' / ') || 'Unknown owner';
}

export function SearchV3AutoSourceOpsPage() {
    const { ready, user } = useAuth();
    const [data, setData] = useState<SearchV3AutoSourceOpsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const inFlightRef = useRef(false);
    const pollTimerRef = useRef<number | null>(null);
    const staff = Boolean(user?.isStaff || isStaffEmail(user?.email));

    const load = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const next = await agentApi.getSearchV3AutoSourceOps({ limit: 60 });
            setData(next);
            setError(null);
        } catch (err) {
            if (!silent) {
                setError(getApiErrorMessage(err, 'Could not load Search v3 auto-source ops.'));
            }
        } finally {
            setLoading(false);
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!ready || !staff) return;
        void load();
    }, [load, ready, staff]);

    useEffect(() => {
        if (!ready || !staff) return undefined;
        const clearPoll = () => {
            if (pollTimerRef.current != null) {
                window.clearTimeout(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        const tick = async () => {
            if (document.visibilityState === 'visible') {
                await load({ silent: true });
            }
            pollTimerRef.current = window.setTimeout(tick, POLL_MS);
        };
        pollTimerRef.current = window.setTimeout(tick, POLL_MS);
        const refreshOnVisible = () => {
            if (document.visibilityState === 'visible') void load({ silent: true });
        };
        document.addEventListener('visibilitychange', refreshOnVisible);
        window.addEventListener('focus', refreshOnVisible);
        return () => {
            clearPoll();
            document.removeEventListener('visibilitychange', refreshOnVisible);
            window.removeEventListener('focus', refreshOnVisible);
        };
    }, [load, ready, staff]);

    const metrics = useMemo(() => {
        if (!data) return [];
        return [
            {
                icon: Clock3,
                label: 'Latest tick',
                value: formatAgeMs(data.scheduler.latestTickAgeMs),
                tone: data.scheduler.observed ? 'green' : 'red',
            },
            {
                icon: TimerReset,
                label: 'Due configs',
                value: formatNumber(data.summary.configs.dueNow),
                tone: data.summary.configs.overdue > 0 ? 'amber' : 'blue',
            },
            {
                icon: ServerCog,
                label: 'Worker queue',
                value: formatNumber(data.summary.workerRuns.queued + data.summary.workerRuns.running),
                tone: data.summary.workerRuns.stuck > 0 ? 'red' : 'violet',
            },
            {
                icon: Zap,
                label: 'Delivered 24h',
                value: formatNumber(data.summary.sourcingRuns.deliveredCount),
                tone: 'coral',
            },
            {
                icon: AlertTriangle,
                label: 'Attention',
                value: formatNumber(data.summary.attentionCount),
                tone: data.summary.attentionCount > 0 ? 'red' : 'green',
            },
        ] satisfies MetricProps[];
    }, [data]);

    if (!ready) return null;

    if (!staff) {
        return (
            <div className="search-v3-ops">
                <style>{styles}</style>
                <div className="search-v3-ops-denied">
                    <ShieldCheck size={24} />
                    <h1>Staff only</h1>
                    <p>This dashboard is available to Lantern internal staff accounts.</p>
                </div>
            </div>
        );
    }

    const health = data ? healthMeta(data.summary.health) : healthMeta('not_observed');
    const HealthIcon = health.icon;

    return (
        <div className="search-v3-ops">
            <style>{styles}</style>
            <header className="search-v3-ops-header">
                <div className="search-v3-ops-title">
                    <SidebarExpandButton style={{ marginLeft: -6 }} />
                    <span className="search-v3-ops-mark">
                        <ServerCog size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                        <h1>Search v3 Auto-source Ops</h1>
                        <p>EventBridge, config claims, worker runs, and sourcing delivery.</p>
                    </div>
                </div>
                <div className="search-v3-ops-actions">
                    <span className="search-v3-ops-health" style={{ color: health.fg, background: health.bg }}>
                        <HealthIcon size={15} />
                        {health.label}
                    </span>
                    <button
                        className="search-v3-ops-icon-btn"
                        type="button"
                        aria-label="Refresh dashboard"
                        title="Refresh dashboard"
                        onClick={() => void load()}
                    >
                        {loading ? <Loader2 size={16} className="search-v3-ops-spin" /> : <RefreshCw size={16} />}
                    </button>
                </div>
            </header>

            {error ? <div className="search-v3-ops-error">{error}</div> : null}

            {loading && !data ? (
                <div className="search-v3-ops-loading">
                    <Loader2 size={18} className="search-v3-ops-spin" />
                    Loading auto-source ops
                </div>
            ) : data ? (
                <>
                    <section className="search-v3-ops-metrics" aria-label="Auto-source health metrics">
                        {metrics.map((metric) => (
                            <Metric key={metric.label} {...metric} />
                        ))}
                    </section>

                    <section className="search-v3-ops-grid">
                        <TickPanel ticks={data.scheduler.recentTicks} />
                        <WorkerPoolPanel pools={data.summary.workerRuns.pools} />
                    </section>

                    <OpsPanel
                        title="Config Queue"
                        action={`${formatNumber(data.summary.configs.active)} active / ${formatNumber(data.summary.configs.overdue)} overdue`}
                    >
                        <ConfigTable rows={data.configs} />
                    </OpsPanel>

                    <OpsPanel
                        title="Run Progress"
                        action={`${formatNumber(data.summary.sourcingRuns.running)} running / ${formatNumber(data.summary.sourcingRuns.queued)} queued`}
                    >
                        <RunTable rows={data.activeRuns} emptyLabel="No active sourcing runs" />
                    </OpsPanel>

                    <OpsPanel
                        title="Worker Queue"
                        action={`${formatNumber(data.summary.workerRuns.stuck)} stuck`}
                    >
                        <WorkerRunTable rows={data.workerRuns} />
                    </OpsPanel>

                    <OpsPanel
                        title="Recent Runs"
                        action={`${formatNumber(data.summary.sourcingRuns.failed24h)} failed 24h`}
                    >
                        <RunTable rows={data.recentRuns} emptyLabel="No recent terminal runs" />
                    </OpsPanel>
                </>
            ) : null}
        </div>
    );
}

type MetricProps = {
    icon: LucideIcon;
    label: string;
    value: string;
    tone: 'blue' | 'green' | 'amber' | 'red' | 'coral' | 'violet';
};

function Metric({ icon: Icon, label, value, tone }: MetricProps) {
    return (
        <div className={`search-v3-ops-metric is-${tone}`}>
            <span>
                <Icon size={16} strokeWidth={1.8} />
            </span>
            <div>
                <strong>{value}</strong>
                <small>{label}</small>
            </div>
        </div>
    );
}

function OpsPanel({
    title,
    action,
    children,
}: {
    title: string;
    action?: string;
    children: ReactNode;
}) {
    return (
        <section className="search-v3-ops-panel">
            <div className="search-v3-ops-panel-head">
                <h2>{title}</h2>
                {action ? <span>{action}</span> : null}
            </div>
            {children}
        </section>
    );
}

function StatusPill({ status }: { status?: string | null }) {
    const tone = statusTone(status);
    return (
        <span className="search-v3-ops-pill" style={{ color: tone.fg, background: tone.bg }}>
            {status || 'unknown'}
        </span>
    );
}

function TickPanel({ ticks }: { ticks: SearchV3AutoSourceTick[] }) {
    return (
        <OpsPanel title="EventBridge Ticks" action={`${ticks.length} recent`}>
            <div className="search-v3-ops-mini-table">
                <div className="search-v3-ops-mini-head">
                    <span>Started</span>
                    <span>Status</span>
                    <span>Counts</span>
                    <span>Duration</span>
                </div>
                {ticks.length === 0 ? (
                    <EmptyRow label="No scheduler ticks observed" />
                ) : (
                    ticks.slice(0, 10).map((tick) => (
                        <div className="search-v3-ops-mini-row" key={tick.id || tick.startedAt}>
                            <span>
                                <strong>{formatDate(tick.startedAt)}</strong>
                                <small>{tick.workerPool || 'unassigned'}</small>
                            </span>
                            <span>
                                <StatusPill status={tick.status} />
                            </span>
                            <span>
                                <strong>{tick.queuedCount} queued</strong>
                                <small>
                                    {tick.scannedCount} scanned / {tick.failedCount} failed
                                </small>
                            </span>
                            <span>{formatDuration(tick.durationMs)}</span>
                        </div>
                    ))
                )}
            </div>
        </OpsPanel>
    );
}

function WorkerPoolPanel({
    pools,
}: {
    pools: Array<{ workerPool: string; status: string; count: number }>;
}) {
    return (
        <OpsPanel title="Worker Pools" action={`${pools.length} buckets`}>
            <div className="search-v3-ops-pool-list">
                {pools.length === 0 ? (
                    <EmptyRow label="No worker run history" />
                ) : (
                    pools.slice(0, 12).map((pool) => (
                        <div
                            className="search-v3-ops-pool-row"
                            key={`${pool.workerPool}:${pool.status}`}
                        >
                            <span>
                                <strong>{pool.workerPool || 'unassigned'}</strong>
                                <small>{pool.status}</small>
                            </span>
                            <b>{formatNumber(pool.count)}</b>
                        </div>
                    ))
                )}
            </div>
        </OpsPanel>
    );
}

function ConfigTable({ rows }: { rows: SearchV3AutoSourceConfigOps[] }) {
    return (
        <div className="search-v3-ops-table is-configs" role="table" aria-label="Search v3 auto-source configs">
            <div className="search-v3-ops-table-head" role="row">
                <span>Search</span>
                <span>Status</span>
                <span>Next run</span>
                <span>Scheduler</span>
                <span>Last queue</span>
                <span>Error</span>
            </div>
            {rows.length === 0 ? (
                <EmptyRow label="No active configs" />
            ) : (
                rows.map((row) => (
                    <div
                        className={`search-v3-ops-table-row ${row.flags.overdue ? 'is-attention' : ''}`}
                        role="row"
                        key={row.id || `${row.chat?.id}:${row.nextRunAt}`}
                    >
                        <span className="search-v3-ops-primary-cell">
                            <strong title={searchLabel(row)}>{searchLabel(row)}</strong>
                            <small title={ownerLabel(row)}>{ownerLabel(row)}</small>
                        </span>
                        <span>
                            <StatusPill status={row.status} />
                        </span>
                        <span>
                            <strong>{formatDate(row.nextRunAt)}</strong>
                            <small>{formatHour(row.scheduleHour)} / {row.dailyTarget}/day</small>
                        </span>
                        <span>
                            <StatusPill status={row.scheduler.status || (row.flags.due ? 'due' : 'idle')} />
                            <small>{row.flags.leased ? 'lease active' : ageFromDate(row.scheduler.lastTickAt)}</small>
                        </span>
                        <span>
                            <strong>{formatDate(row.scheduler.lastQueuedAt)}</strong>
                            <small>{row.scheduler.lastQueuedAgentRunId || row.scheduler.lastQueuedRunId || 'No run id'}</small>
                        </span>
                        <span className="search-v3-ops-truncate" title={row.scheduler.lastErrorMessage || ''}>
                            {row.scheduler.lastErrorMessage || '-'}
                        </span>
                    </div>
                ))
            )}
        </div>
    );
}

function RunTable({
    rows,
    emptyLabel,
}: {
    rows: SearchV3AutoSourceRunOps[];
    emptyLabel: string;
}) {
    return (
        <div className="search-v3-ops-table is-runs" role="table" aria-label="Search v3 auto-source runs">
            <div className="search-v3-ops-table-head" role="row">
                <span>Search</span>
                <span>Status</span>
                <span>Counts</span>
                <span>Worker</span>
                <span>Updated</span>
                <span>Diagnosis</span>
            </div>
            {rows.length === 0 ? (
                <EmptyRow label={emptyLabel} />
            ) : (
                rows.map((row) => (
                    <div
                        className={`search-v3-ops-table-row ${row.flags.stuck ? 'is-attention' : ''}`}
                        role="row"
                        key={row.id || `${row.chat?.id}:${row.createdAt}`}
                    >
                        <span className="search-v3-ops-primary-cell">
                            <strong title={searchLabel(row)}>{searchLabel(row)}</strong>
                            <small title={ownerLabel(row)}>{ownerLabel(row)}</small>
                        </span>
                        <span>
                            <StatusPill status={row.status} />
                            {row.flags.stuck ? <small>{row.flags.reason}</small> : null}
                        </span>
                        <span>
                            <strong>{row.deliveredCount}/{row.targetCount} delivered</strong>
                            <small>
                                {row.scannedCount} scanned / {row.scoredCount} scored / {row.bufferedCount} buffered
                            </small>
                        </span>
                        <span>
                            <strong>{row.agentRun?.workerPool || 'unassigned'}</strong>
                            <small>{row.agentRun?.status || row.agentRunId || 'No agent run'}</small>
                        </span>
                        <span>
                            <strong>{ageFromDate(row.updatedAt || row.endedAt || row.startedAt)}</strong>
                            <small>{formatDate(row.startedAt || row.createdAt)}</small>
                        </span>
                        <span
                            className="search-v3-ops-truncate"
                            title={row.errorMessage || row.progress?.message || row.diagnosisMessage || row.exhaustionReason || ''}
                        >
                            {row.errorMessage || row.progress?.message || row.diagnosisMessage || row.exhaustionReason || '-'}
                        </span>
                    </div>
                ))
            )}
        </div>
    );
}

function WorkerRunTable({ rows }: { rows: SearchV3AutoSourceWorkerRunOps[] }) {
    return (
        <div className="search-v3-ops-table is-workers" role="table" aria-label="Search v3 auto-source worker runs">
            <div className="search-v3-ops-table-head" role="row">
                <span>Search</span>
                <span>Status</span>
                <span>Worker</span>
                <span>Heartbeat</span>
                <span>Attempts</span>
                <span>Error</span>
            </div>
            {rows.length === 0 ? (
                <EmptyRow label="No active or recent failed worker runs" />
            ) : (
                rows.map((row) => (
                    <div
                        className={`search-v3-ops-table-row ${row.flags.stuck ? 'is-attention' : ''}`}
                        role="row"
                        key={row.id || `${row.chat?.id}:${row.startedAt}`}
                    >
                        <span className="search-v3-ops-primary-cell">
                            <strong title={searchLabel(row)}>{searchLabel(row)}</strong>
                            <small title={ownerLabel(row)}>{ownerLabel(row)}</small>
                        </span>
                        <span>
                            <StatusPill status={row.status} />
                            {row.flags.stuck ? <small>{row.flags.reason}</small> : null}
                        </span>
                        <span>
                            <strong>{row.workerPool || 'unassigned'}</strong>
                            <small>{row.leaseOwner || 'No lease'}</small>
                        </span>
                        <span>
                            <strong>{ageFromDate(row.heartbeatAt || row.updatedAt)}</strong>
                            <small>{row.leaseUntil ? `lease ${formatDate(row.leaseUntil)}` : 'No lease expiry'}</small>
                        </span>
                        <span>{formatNumber(row.attempts)}</span>
                        <span className="search-v3-ops-truncate" title={row.errorMessage || row.progress?.message || ''}>
                            {row.errorMessage || row.progress?.message || '-'}
                        </span>
                    </div>
                ))
            )}
        </div>
    );
}

function EmptyRow({ label }: { label: string }) {
    return <div className="search-v3-ops-empty">{label}</div>;
}

const styles = `
.search-v3-ops {
    min-height: 100%;
    padding: 28px 34px 48px;
    background: linear-gradient(180deg, #FBFBFA 0%, #F7F9FB 52%, ${C.bg} 100%);
    color: ${C.ink};
    font-family: var(--font-sans);
}
.search-v3-ops-header,
.search-v3-ops-panel,
.search-v3-ops-metric,
.search-v3-ops-error,
.search-v3-ops-loading,
.search-v3-ops-denied {
    border: 1px solid ${C.line};
    border-radius: 8px;
    box-shadow: 0 16px 44px rgba(31, 36, 40, 0.05);
}
.search-v3-ops-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 18px 20px;
    background: ${C.header};
    backdrop-filter: blur(18px);
}
.search-v3-ops-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 13px;
}
.search-v3-ops-mark {
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 8px;
    color: ${C.blue};
    background: ${C.blueSoft};
}
.search-v3-ops-title h1,
.search-v3-ops-panel h2,
.search-v3-ops-denied h1 {
    margin: 0;
    color: ${C.ink};
    letter-spacing: 0;
}
.search-v3-ops-title h1 {
    font-size: 22px;
    line-height: 1.15;
    font-weight: 760;
}
.search-v3-ops-title p,
.search-v3-ops-denied p {
    margin: 4px 0 0;
    color: ${C.muted};
    font-size: 13px;
}
.search-v3-ops-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}
.search-v3-ops-health,
.search-v3-ops-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    max-width: 100%;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 720;
    line-height: 1;
    white-space: nowrap;
}
.search-v3-ops-health {
    min-height: 34px;
    padding: 0 12px;
}
.search-v3-ops-pill {
    width: fit-content;
    min-height: 24px;
    padding: 0 9px;
}
.search-v3-ops-icon-btn {
    display: inline-flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    border: 1px solid ${C.line};
    border-radius: 8px;
    color: ${C.ink};
    background: rgba(255,255,255,0.78);
    cursor: pointer;
}
.search-v3-ops-icon-btn:hover {
    background: #fff;
}
.search-v3-ops-error,
.search-v3-ops-loading,
.search-v3-ops-denied {
    margin-top: 16px;
    padding: 18px;
    background: ${C.panelSolid};
}
.search-v3-ops-error {
    color: ${C.red};
    background: ${C.redSoft};
}
.search-v3-ops-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${C.muted};
}
.search-v3-ops-denied {
    max-width: 460px;
    margin: 72px auto 0;
    text-align: center;
}
.search-v3-ops-denied svg {
    color: ${C.red};
}
.search-v3-ops-metrics {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 16px;
}
.search-v3-ops-metric {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: ${C.panel};
    backdrop-filter: blur(14px);
}
.search-v3-ops-metric > span {
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: 8px;
}
.search-v3-ops-metric div {
    min-width: 0;
}
.search-v3-ops-metric strong {
    display: block;
    overflow: hidden;
    color: ${C.ink};
    font-size: 20px;
    font-weight: 780;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.search-v3-ops-metric small {
    display: block;
    margin-top: 3px;
    color: ${C.muted};
    font-size: 12px;
}
.search-v3-ops-metric.is-blue > span { color: ${C.blue}; background: ${C.blueSoft}; }
.search-v3-ops-metric.is-green > span { color: ${C.green}; background: ${C.greenSoft}; }
.search-v3-ops-metric.is-amber > span { color: ${C.amber}; background: ${C.amberSoft}; }
.search-v3-ops-metric.is-red > span { color: ${C.red}; background: ${C.redSoft}; }
.search-v3-ops-metric.is-coral > span { color: ${C.coral}; background: ${C.coralSoft}; }
.search-v3-ops-metric.is-violet > span { color: ${C.violet}; background: ${C.violetSoft}; }
.search-v3-ops-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
    gap: 14px;
    margin-top: 14px;
}
.search-v3-ops-panel {
    margin-top: 14px;
    overflow: hidden;
    background: ${C.panel};
    backdrop-filter: blur(16px);
}
.search-v3-ops-grid .search-v3-ops-panel {
    margin-top: 0;
}
.search-v3-ops-panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid ${C.line};
    background: rgba(255,255,255,0.68);
}
.search-v3-ops-panel-head h2 {
    font-size: 15px;
    line-height: 1.2;
    font-weight: 760;
}
.search-v3-ops-panel-head span {
    color: ${C.muted};
    font-size: 12px;
    font-weight: 680;
}
.search-v3-ops-mini-table,
.search-v3-ops-pool-list {
    padding: 8px;
}
.search-v3-ops-mini-head,
.search-v3-ops-mini-row {
    display: grid;
    grid-template-columns: minmax(130px, 1.2fr) minmax(98px, 0.7fr) minmax(120px, 0.9fr) minmax(80px, 0.5fr);
    align-items: center;
    gap: 10px;
}
.search-v3-ops-mini-head,
.search-v3-ops-table-head {
    color: ${C.faint};
    font-size: 11px;
    font-weight: 740;
    text-transform: uppercase;
}
.search-v3-ops-mini-head {
    padding: 7px 9px;
}
.search-v3-ops-mini-row,
.search-v3-ops-pool-row {
    min-height: 54px;
    padding: 9px;
    border-radius: 8px;
    color: ${C.ink};
}
.search-v3-ops-mini-row:hover,
.search-v3-ops-pool-row:hover,
.search-v3-ops-table-row:hover {
    background: ${C.rowHover};
}
.search-v3-ops-mini-row strong,
.search-v3-ops-pool-row strong,
.search-v3-ops-table-row strong {
    display: block;
    overflow: hidden;
    font-size: 12px;
    font-weight: 740;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.search-v3-ops-mini-row small,
.search-v3-ops-pool-row small,
.search-v3-ops-table-row small {
    display: block;
    overflow: hidden;
    margin-top: 3px;
    color: ${C.muted};
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.search-v3-ops-pool-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.search-v3-ops-pool-row b {
    color: ${C.ink};
    font-size: 16px;
}
.search-v3-ops-table {
    min-width: 980px;
}
.search-v3-ops-table-head,
.search-v3-ops-table-row {
    display: grid;
    align-items: center;
    gap: 12px;
}
.search-v3-ops-table.is-configs .search-v3-ops-table-head,
.search-v3-ops-table.is-configs .search-v3-ops-table-row {
    grid-template-columns: minmax(220px, 1.35fr) minmax(90px, 0.55fr) minmax(145px, 0.85fr) minmax(132px, 0.8fr) minmax(155px, 0.9fr) minmax(150px, 1fr);
}
.search-v3-ops-table.is-runs .search-v3-ops-table-head,
.search-v3-ops-table.is-runs .search-v3-ops-table-row,
.search-v3-ops-table.is-workers .search-v3-ops-table-head,
.search-v3-ops-table.is-workers .search-v3-ops-table-row {
    grid-template-columns: minmax(220px, 1.35fr) minmax(110px, 0.7fr) minmax(160px, 0.95fr) minmax(130px, 0.8fr) minmax(120px, 0.7fr) minmax(160px, 1fr);
}
.search-v3-ops-table-head {
    min-height: 38px;
    padding: 0 14px;
    border-bottom: 1px solid ${C.line};
    background: rgba(255,255,255,0.56);
}
.search-v3-ops-table-row {
    min-height: 66px;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(226,231,236,0.72);
}
.search-v3-ops-table-row.is-attention {
    box-shadow: inset 3px 0 0 ${C.amber};
}
.search-v3-ops-primary-cell {
    min-width: 0;
}
.search-v3-ops-truncate {
    display: block;
    overflow: hidden;
    color: ${C.muted};
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.search-v3-ops-empty {
    display: flex;
    min-height: 74px;
    align-items: center;
    justify-content: center;
    padding: 18px;
    color: ${C.muted};
    font-size: 13px;
}
.search-v3-ops-spin {
    animation: search-v3-ops-spin 0.85s linear infinite;
}
@keyframes search-v3-ops-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
@media (max-width: 1180px) {
    .search-v3-ops-metrics {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .search-v3-ops-grid {
        grid-template-columns: 1fr;
    }
    .search-v3-ops-panel {
        overflow-x: auto;
    }
}
@media (max-width: 720px) {
    .search-v3-ops {
        padding: 18px 14px 36px;
    }
    .search-v3-ops-header {
        align-items: flex-start;
        flex-direction: column;
    }
    .search-v3-ops-actions {
        width: 100%;
        justify-content: space-between;
    }
    .search-v3-ops-metrics {
        grid-template-columns: 1fr;
    }
    .search-v3-ops-title h1 {
        font-size: 19px;
    }
    .search-v3-ops-title p {
        font-size: 12px;
    }
}
`;
